/**
 * Decision Card - Cinematic choice presentation
 *
 * Design principles:
 * - Cards feel like moments of judgment, not form inputs
 * - Visual encoding of risk (subtle, not labeled)
 * - Micro-interactions create emotional memory
 * - Spatial connection to scene actions
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { theme } from '@core/theme';
import type { DecisionOption } from '@data/types';

export type RiskLevel = 'safe' | 'caution' | 'risky' | 'neutral';

export interface DecisionCardConfig {
  option: DecisionOption;
  width?: number;
  index: number;
  riskLevel?: RiskLevel;
}

const CARD_WIDTH = 300;
const CARD_PADDING = 20;
const MIN_HEIGHT = 110;
const HOVER_LIFT = 8;
const SELECT_SCALE = 1.02;

export class DecisionCard extends Container {
  private option: DecisionOption;
  private cardWidth: number;
  private index: number;
  private riskLevel: RiskLevel;

  private background: Graphics;
  private accentBar: Graphics;
  private labelText: Text;
  private descriptionText: Text | null = null;
  private keyHint: Container;
  private glowEffect: Graphics;

  private isHovered = false;
  private isSelected = false;
  private isDisabled = false;
  private onSelect: ((optionId: string) => void) | null = null;

  // Animation state
  private targetY = 0;
  private currentY = 0;
  private targetScale = 1;
  private currentScale = 1;

  constructor(config: DecisionCardConfig) {
    super();

    this.option = config.option;
    this.cardWidth = config.width || CARD_WIDTH;
    this.index = config.index;
    this.riskLevel = config.riskLevel || this.inferRiskLevel();

    // Glow effect layer (behind everything)
    this.glowEffect = new Graphics();
    this.addChild(this.glowEffect);

    // Background card
    this.background = new Graphics();
    this.addChild(this.background);

    // Accent bar (left edge - risk indicator)
    this.accentBar = new Graphics();
    this.addChild(this.accentBar);

    // Keyboard hint badge
    this.keyHint = this.createKeyHint();
    this.addChild(this.keyHint);

    // Label text - action-oriented, first person
    const labelStyle = new TextStyle({
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.sizes.base,
      fontWeight: theme.typography.weights.semibold,
      fill: theme.colors.textPrimary,
      wordWrap: true,
      wordWrapWidth: this.cardWidth - CARD_PADDING * 2 - 48,
      lineHeight: theme.typography.sizes.base * theme.typography.lineHeight.normal,
    });

    this.labelText = new Text({ text: this.option.label, style: labelStyle });
    this.labelText.position.set(CARD_PADDING + 40, CARD_PADDING);
    this.addChild(this.labelText);

    // Description (if provided)
    if (this.option.description) {
      const descStyle = new TextStyle({
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.sizes.sm,
        fill: theme.colors.textSecondary,
        wordWrap: true,
        wordWrapWidth: this.cardWidth - CARD_PADDING * 2 - 48,
        lineHeight: theme.typography.sizes.sm * theme.typography.lineHeight.relaxed,
      });

      this.descriptionText = new Text({ text: this.option.description, style: descStyle });
      this.descriptionText.position.set(CARD_PADDING + 40, CARD_PADDING + this.labelText.height + 8);
      this.addChild(this.descriptionText);
    }

    // Start visible (entrance animation optional)
    this.alpha = 1;
    this.scale.set(1);

    this.draw();
    this.setupInteraction();
  }

  /**
   * Infer risk level from option text (heuristic)
   */
  private inferRiskLevel(): RiskLevel {
    const label = this.option.label.toLowerCase();

    // Safe indicators
    if (label.includes('wait') || label.includes('stop') || label.includes('eye contact') ||
        label.includes('let') || label.includes('stay')) {
      return 'safe';
    }

    // Risky indicators
    if (label.includes('run') || label.includes('quickly') || label.includes('rush') ||
        label.includes('ignore') || label.includes('assume')) {
      return 'risky';
    }

    // Caution indicators
    if (label.includes('proceed') || label.includes('start') || label.includes('cross') ||
        label.includes('go')) {
      return 'caution';
    }

    return 'neutral';
  }

  private createKeyHint(): Container {
    const hint = new Container();

    const bg = new Graphics();
    bg.circle(0, 0, 14);
    bg.fill(theme.colors.decision);
    hint.addChild(bg);

    const text = new Text({
      text: String(this.index + 1),
      style: new TextStyle({
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.sizes.xs,
        fontWeight: theme.typography.weights.bold,
        fill: theme.colors.textDark,
      }),
    });
    text.anchor.set(0.5);
    hint.addChild(text);

    hint.position.set(CARD_PADDING + 14, CARD_PADDING + 10);
    return hint;
  }

  private calculateHeight(): number {
    let height = CARD_PADDING * 2 + this.labelText.height;
    if (this.descriptionText) {
      height += this.descriptionText.height + 8;
    }
    return Math.max(height, MIN_HEIGHT);
  }

  private draw(): void {
    const height = this.calculateHeight();
    const riskConfig = theme.riskEncoding[this.riskLevel];

    // Determine colors based on state
    let bgColor: number = theme.cardStates.default.bg;
    let borderColor: number = theme.cardStates.default.border;
    let borderWidth = 1;
    let shadowAlpha = 0.15;
    let shadowOffset = 4;
    let glowAlpha = 0;

    if (this.isDisabled) {
      bgColor = theme.colors.neutralDark;
      borderColor = theme.colors.neutralDark;
      shadowAlpha = 0.05;
    } else if (this.isSelected) {
      bgColor = theme.cardStates.selected.bg;
      borderColor = theme.colors.decision;
      borderWidth = 2;
      shadowAlpha = 0.25;
      this.targetScale = SELECT_SCALE;
      glowAlpha = 0.3;
    } else if (this.isHovered) {
      bgColor = theme.cardStates.hover.bg;
      borderColor = riskConfig.accentColor;
      borderWidth = 2;
      shadowAlpha = 0.2;
      shadowOffset = HOVER_LIFT;
      this.targetY = -HOVER_LIFT;
      glowAlpha = riskConfig.borderGlow;
    } else {
      this.targetY = 0;
      this.targetScale = 1;
    }

    // Smooth animation interpolation
    this.currentY += (this.targetY - this.currentY) * 0.2;
    this.currentScale += (this.targetScale - this.currentScale) * 0.15;

    // Apply scale
    this.scale.set(this.currentScale);

    // Draw glow effect (simple, only on hover)
    this.glowEffect.clear();
    if (glowAlpha > 0 && this.isHovered) {
      this.glowEffect.roundRect(-4, this.currentY - 4, this.cardWidth + 8, height + 8, theme.radii.lg + 2);
      this.glowEffect.fill({ color: riskConfig.accentColor, alpha: glowAlpha * 0.3 });
    }

    // Draw background
    this.background.clear();

    // Shadow
    this.background.roundRect(2, shadowOffset + this.currentY + 2, this.cardWidth, height, theme.radii.lg);
    this.background.fill({ color: 0x000000, alpha: shadowAlpha });

    // Main card
    this.background.roundRect(0, this.currentY, this.cardWidth, height, theme.radii.lg);
    this.background.fill(bgColor);

    // Border
    this.background.roundRect(0, this.currentY, this.cardWidth, height, theme.radii.lg);
    this.background.stroke({ width: borderWidth, color: borderColor, alpha: 0.9 });

    // Draw accent bar (left edge - subtle risk indicator)
    this.accentBar.clear();
    if (!this.isDisabled) {
      const barHeight = height - 24;
      const barY = this.currentY + 12;

      // Accent bar with risk color
      this.accentBar.roundRect(6, barY, 3, barHeight, 2);
      this.accentBar.fill({ color: riskConfig.accentColor, alpha: this.isHovered ? 0.8 : 0.4 });
    }

    // Update text positions
    this.labelText.position.y = CARD_PADDING + this.currentY;
    if (this.descriptionText) {
      this.descriptionText.position.y = CARD_PADDING + this.labelText.height + 8 + this.currentY;
    }
    this.keyHint.position.y = CARD_PADDING + 10 + this.currentY;

    // Update text colors
    if (this.isDisabled) {
      this.labelText.style.fill = theme.colors.textMuted;
      if (this.descriptionText) {
        this.descriptionText.style.fill = theme.colors.textMuted;
      }
    } else if (this.isSelected) {
      this.labelText.style.fill = theme.colors.decision;
      if (this.descriptionText) {
        this.descriptionText.style.fill = theme.colors.textSecondary;
      }
    } else if (this.isHovered) {
      this.labelText.style.fill = theme.colors.textPrimary;
      if (this.descriptionText) {
        this.descriptionText.style.fill = theme.colors.textSecondary;
      }
    } else {
      this.labelText.style.fill = theme.colors.textPrimary;
      if (this.descriptionText) {
        this.descriptionText.style.fill = theme.colors.textSecondary;
      }
    }

    // Update key hint
    const hintBg = this.keyHint.children[0] as Graphics;
    hintBg.clear();
    hintBg.circle(0, 0, 14);

    if (this.isSelected) {
      hintBg.fill(theme.colors.decision);
    } else if (this.isHovered) {
      hintBg.fill(theme.colors.decisionHover);
    } else {
      hintBg.fill({ color: theme.colors.neutral, alpha: 0.8 });
    }
  }

  private setupInteraction(): void {
    this.eventMode = 'static';
    this.cursor = 'pointer';

    this.on('pointerover', () => {
      if (this.isDisabled || this.isSelected) return;
      this.isHovered = true;
      this.animateState();
    });

    this.on('pointerout', () => {
      this.isHovered = false;
      this.animateState();
    });

    this.on('pointerdown', () => {
      if (this.isDisabled) return;
      this.select();
    });
  }

  /**
   * Animate hover state changes (on-demand, not continuous)
   */
  private animateState(): void {
    const animate = () => {
      const yDiff = Math.abs(this.targetY - this.currentY);
      const scaleDiff = Math.abs(this.targetScale - this.currentScale);

      if (yDiff > 0.1 || scaleDiff > 0.001) {
        this.draw();
        requestAnimationFrame(animate);
      } else {
        this.currentY = this.targetY;
        this.currentScale = this.targetScale;
        this.draw();
      }
    };
    requestAnimationFrame(animate);
  }

  /**
   * Simple entrance - no animation for performance
   */
  animateEntrance(_delay = 0): void {
    // Entrance animation disabled for performance
    this.alpha = 1;
    this.scale.set(1);
  }

  select(): void {
    if (this.isSelected || this.isDisabled) return;

    this.isSelected = true;
    this.isHovered = false;

    // Micro-pause effect (150ms) before triggering
    setTimeout(() => {
      this.onSelect?.(this.option.id);
    }, 150);
  }

  deselect(): void {
    this.isSelected = false;
    this.draw();
  }

  setDisabled(disabled: boolean): void {
    this.isDisabled = disabled;
    this.eventMode = disabled ? 'none' : 'static';
    this.cursor = disabled ? 'default' : 'pointer';
    this.alpha = disabled ? 0.4 : 1;
    this.draw();
  }

  setOnSelect(callback: (optionId: string) => void): void {
    this.onSelect = callback;
  }

  getOptionId(): string {
    return this.option.id;
  }

  getHeight(): number {
    return this.calculateHeight();
  }

  getKeyboardIndex(): number {
    return this.index + 1;
  }

  getRiskLevel(): RiskLevel {
    return this.riskLevel;
  }
}
